import React, { useState, useEffect } from 'react';
import './VirtualMallIncentive.scss';
import { virtualMallIncentiveListApi, virtualMallRecentTransactionsApi, virtualMallSingleIdProductOrderHistoryApi, virtualMallShopPurchaseIncentiveApi, virtualMallWithdrawRequestApi } from './VirtualMallApi/api';
import { toast } from 'react-toastify';
import { RotatingLines } from 'react-loader-spinner';

export default function VirtualMallIncentive({ canViewShopPurchaseIncentive = false }) {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [selectedWallet, setSelectedWallet] = useState('Incentive Wallet');
    const [stats, setStats] = useState({
        totalEarnings: 0,
        thisMonth: 0,
        pending: 0,
        rank: 'N/A'
    });
    const [transactions, setTransactions] = useState([]);
    const [walletBalance, setWalletBalance] = useState(0);
    const [activityWallet, setActivityWallet] = useState(0);
    const [cashbackWallet, setCashbackWallet] = useState(0); // Dummy data
    const [shopPurchaseIncentive, setShopPurchaseIncentive] = useState(0);

    // Search and Pagination states
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchIncentiveList = async () => {
            try {
                setLoading(true);
                const [walletResponse, transactionsResponse, productOrdersResponse] = await Promise.all([
                    virtualMallIncentiveListApi(),
                    virtualMallRecentTransactionsApi(),
                    virtualMallSingleIdProductOrderHistoryApi().catch((error) => {
                        console.error("Failed to load Single ID product purchase history:", error);
                        return null;
                    })
                ]);

                if (walletResponse && (walletResponse.status || walletResponse.result)) {
                    setWalletBalance(parseFloat(walletResponse.incentiveWallet) || 0);
                    setActivityWallet(parseFloat(walletResponse.activityWallet) || 0);
                    setCashbackWallet(parseFloat(walletResponse.cashbackWallet) || 0);
                    const incentiveData = Array.isArray(walletResponse.data) ? walletResponse.data : [];
                    calculateStats(incentiveData);
                }

                if (transactionsResponse && (transactionsResponse.status || transactionsResponse.result)) {
                    const recentTransactions = Array.isArray(transactionsResponse.data) ? transactionsResponse.data : [];
                    const productOrderTransactions = productOrdersResponse?.status && Array.isArray(productOrdersResponse.data)
                        ? productOrdersResponse.data.map((order) => ({
                            virtual_wallethist_id: `single-product-order-${order.order_id}`,
                            vu_referal_id: order.purchaser_referral_id,
                            vu_name: order.purchaser_name,
                            vu_mobile: order.purchaser_mobile,
                            created_at: order.created_at,
                            virtual_wallethist_remark: `Single ID product purchase: ${order.product_name} (${order.order_status})`,
                            virtual_wallethist_incentive_amount: order.shop_purchase_amount,
                            type: 'single-id-product-purchase'
                        }))
                        : [];
                    setTransactions([...recentTransactions, ...productOrderTransactions]
                        .sort((a, b) => new Date(b.created_at || b.date) - new Date(a.created_at || a.date)));
                }
            } catch (error) {
                console.error("Error fetching virtual mall incentive list:", error);
                toast.error("Failed to load incentive data");
            } finally {
                setLoading(false);
            }
        };
        fetchIncentiveList();
    }, []);

    useEffect(() => {
        if (!canViewShopPurchaseIncentive) {
            setShopPurchaseIncentive(0);
            return;
        }

        virtualMallShopPurchaseIncentiveApi()
            .then((response) => {
                if (response?.status) setShopPurchaseIncentive(parseFloat(response.balance) || 0);
            })
            .catch((error) => {
                console.error("Failed to load Shop Purchase Incentive balance:", error);
            });
    }, [canViewShopPurchaseIncentive]);

    const calculateStats = (data) => {
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        let total = 0;
        let monthTotal = 0;

        data.forEach(item => {
            const amount = parseFloat(item.virtual_wallethist_incentive_amount || 0);
            const date = new Date(item.created_at);

            total += amount;
            if (date.getMonth() === currentMonth && date.getFullYear() === currentYear) {
                monthTotal += amount;
            }
        });

        setStats({
            totalEarnings: total,
            thisMonth: monthTotal,
            pending: 0,
            rank: 'N/A'
        });
    };

    const handleWithdraw = async (e) => {
        e.preventDefault();
        if (!withdrawAmount || isNaN(withdrawAmount) || parseFloat(withdrawAmount) <= 0) {
            toast.error("Please enter a valid amount");
            return;
        }

        try {
            setSubmitting(true);
            const res = await virtualMallWithdrawRequestApi({ amount: withdrawAmount, wallet_type: selectedWallet });
            if (res.result === true) {
                toast.success(res.message || "Withdrawal request submitted successfully!");
                setShowModal(false);
                setWithdrawAmount('');
                // Optionally refresh data to update balance
            } else {
                toast.error(res.message || "Failed to submit request");
            }
        } catch (error) {
            console.error(error);
            toast.error("An error occurred. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    // Filter and Paginate logic
    const filteredTransactions = transactions.filter(item =>
    (item.vu_referal_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.vu_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.virtual_wallethist_remark?.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredTransactions.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

    if (loading) {
        return (
            <div className="dashboard-section" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <RotatingLines
                    strokeColor="#667eea"
                    strokeWidth="5"
                    animationDuration="0.75"
                    width="48"
                    visible={true}
                />
            </div>
        );
    }

    return (
        <div className="dashboard-section virtual-mall-incentive">
            <div className="section-header">
                <h2>Incentive & Earnings</h2>
                <button className="custom-btns" onClick={() => setShowModal(true)}>
                    <i className="fas fa-plus"></i> Request Withdrawal
                </button>
            </div>

            <div className="stats-grid">
                <div className="stat-card earnings">
                    <div className="stat-icon">
                        <i className="fas fa-wallet"></i>
                    </div>
                    <div className="stat-content">
                        <div className="stat-label">Total Earnings</div>
                        <div className="stat-value">₹{stats.totalEarnings.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>
                <div className="stat-card monthly">
                    <div className="stat-icon">
                        <i className="fas fa-calendar-alt"></i>
                    </div>
                    <div className="stat-content">
                        <div className="stat-label">This Month</div>
                        <div className="stat-value">₹{stats.thisMonth.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>
                <div className="stat-card balance">
                    <div className="stat-icon">
                        <i className="fas fa-coins"></i>
                    </div>
                    <div className="stat-content">
                        <div className="stat-label">Wallet Balance</div>
                        <div className="stat-value">₹{(walletBalance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>
                <div className="stat-card activity">
                    <div className="stat-icon">
                        <i className="fas fa-chart-line"></i>
                    </div>
                    <div className="stat-content">
                        <div className="stat-label">Activity Point </div>
                        <div className="stat-value">{(activityWallet || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>
                <div className="stat-card cashback">
                    <div className="stat-icon">
                        <i className="fas fa-hand-holding-usd"></i>
                    </div>
                    <div className="stat-content">
                        <div className="stat-label">Cashback Wallet</div>
                        <div className="stat-value">₹{(cashbackWallet || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                    </div>
                </div>
                {canViewShopPurchaseIncentive && (
                    <div className="stat-card shop-purchase-incentive">
                        <div className="stat-icon">
                            <i className="fas fa-store"></i>
                        </div>
                        <div className="stat-content">
                            <div className="stat-label">Shop Purchase Incentive</div>
                            <div className="stat-value">₹{shopPurchaseIncentive.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="table-section">
                <div className="table-header">
                    <h3>Recent Transactions</h3>
                    <div className="search-box">
                        <i className="fas fa-search"></i>
                        <input
                            type="text"
                            placeholder="Search by ID, Name or Remark..."
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setCurrentPage(1); // Reset to first page on search
                            }}
                        />
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Referral ID</th>
                                <th>Name</th>
                                <th>Mobile No</th>
                                <th>Date</th>
                                <th>Description</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((item, index) => (
                                    <tr key={item.virtual_wallethist_id || index}>
                                        <td>{indexOfFirstItem + index + 1}</td>
                                        <td className="font-weight-bold">{item.vu_referal_id || 'N/A'}</td>
                                        <td>{item.vu_name || 'N/A'}</td>
                                        <td>{item.vu_mobile || 'N/A'}</td>
                                        <td>{item.created_at ? new Date(item.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A'}</td>
                                        <td>
                                            <span className="remark-text">{item.virtual_wallethist_remark || ''}</span>
                                        </td>
                                        <td className="text-right amount-cell">
                                            {item.virtual_wallethist_remark && (item.virtual_wallethist_remark.toLowerCase().includes('pts') || item.virtual_wallethist_remark.toLowerCase().startsWith('manual admin edit for')) 
                                                ? `${parseFloat(item.virtual_wallethist_incentive_amount || 0)} points` 
                                                : `₹${parseFloat(item.virtual_wallethist_incentive_amount || 0).toFixed(2)}`
                                            }
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                                        <div className="no-data">
                                            <i className="fas fa-folder-open" style={{ fontSize: '24px', marginBottom: '10px', display: 'block', opacity: 0.5 }}></i>
                                            No transactions found
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="pagination-wrapper">
                        <div className="pagination-info">
                            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredTransactions.length)} of {filteredTransactions.length} entries
                        </div>
                        <div className="pagination-controls">
                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>

                            {[...Array(totalPages)].map((_, i) => {
                                // Logic to show limited page numbers if there are too many
                                if (totalPages > 7) {
                                    if (i + 1 === 1 || i + 1 === totalPages || (i + 1 >= currentPage - 2 && i + 1 <= currentPage + 2)) {
                                        return (
                                            <button
                                                key={i}
                                                className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                                onClick={() => handlePageChange(i + 1)}
                                            >
                                                {i + 1}
                                            </button>
                                        );
                                    } else if (i + 1 === currentPage - 3 || i + 1 === currentPage + 3) {
                                        return <span key={i} className="pagination-ellipsis">...</span>;
                                    }
                                    return null;
                                }
                                return (
                                    <button
                                        key={i}
                                        className={`page-btn ${currentPage === i + 1 ? 'active' : ''}`}
                                        onClick={() => handlePageChange(i + 1)}
                                    >
                                        {i + 1}
                                    </button>
                                );
                            })}

                            <button
                                className="page-btn"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Withdrawal Modal */}
            {
                showModal && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Request Withdrawal</h5>
                                <button type="button" className="close-btn" onClick={() => setShowModal(false)}>&times;</button>
                            </div>
                            <div className="modal-body enhanced-withdrawal">
                                <form onSubmit={handleWithdraw}>
                                    <div className="deduction-alert">
                                        <span className="alert-icon">💡</span>
                                        <p>Note: Your withdrawal request will be processed and credited to your account after verification.</p>
                                    </div>
                                    
                                    <div className="form-group mb-3">
                                        <label>Select Wallet</label>
                                        <select 
                                            className="wallet-select" 
                                            value={selectedWallet} 
                                            onChange={(e) => {
                                                setSelectedWallet(e.target.value);
                                                setWithdrawAmount('');
                                            }}
                                        >
                                            <option value="Incentive Wallet">Incentive Wallet</option>
                                            <option value="Cashback Wallet">Cashback Wallet</option>
                                        </select>
                                    </div>

                                    <div className="mt-2 mb-3">
                                        <div className="balance-info-card">
                                            <span className="label">Available Balance</span>
                                            <span className="value">₹{selectedWallet === 'Cashback Wallet' ? (cashbackWallet || 0).toFixed(2) : (walletBalance || 0).toFixed(2)}</span>
                                        </div>
                                    </div>

                                    <div className="form-group amount-input-group">
                                        <label>Withdrawal Amount</label>
                                        <div className="input-with-symbol">
                                            <span className="currency-prefix">₹</span>
                                            <input
                                                type="number"
                                                className="form-control"
                                                placeholder="0.00"
                                                value={withdrawAmount}
                                                onChange={(e) => setWithdrawAmount(e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="modal-footer">
                                        <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={submitting || !withdrawAmount || parseFloat(withdrawAmount) <= 0 || parseFloat(withdrawAmount) > (selectedWallet === 'Cashback Wallet' ? cashbackWallet : walletBalance)}
                                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                                        >
                                            {submitting ? (
                                                <>
                                                    <RotatingLines
                                                        strokeColor="white"
                                                        strokeWidth="5"
                                                        animationDuration="0.75"
                                                        width="16"
                                                        visible={true}
                                                    />
                                                    Submitting...
                                                </>
                                            ) : 'Submit Withdrawal'}
                                        </button>
                                    </div>
                                    {withdrawAmount > (selectedWallet === 'Cashback Wallet' ? cashbackWallet : walletBalance) && (
                                        <p className="insufficient-funds">Insufficient funds in selected wallet.</p>
                                    )}
                                </form>
                            </div>
                        </div>
                    </div>
                )
            }
        </div>
    );
}
